import { useEffect, useState } from "react";
import { api, fmtMoney, STATUS_LABELS, fmtDate } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { Briefcase, ListChecks, AlertTriangle, TrendingUp, Users2, FileText, ArrowUpRight, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { toast } from "sonner";

const STATUS_COLORS = ["#0A2540", "#3B82F6", "#10B981", "#E05A3D", "#F59E0B", "#94A3B8"];

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <div className="text-slate-500">Chargement…</div>;

  const k = stats.kpis;
  const statusData = (stats.projets_par_statut || []).map((s) => ({
    name: STATUS_LABELS[s.status] || s.status, value: s.count,
  }));
  const budgetData = [
    { name: "Prévu", value: k.budget_prevu_total },
    { name: "Réel", value: k.budget_reel_total },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Vue Direction</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">Pilotage en temps réel de vos missions BTP</p>
        </div>
        <button onClick={() => navigate("/projects")} className="bg-btp-navy hover:bg-btp-navy-hover text-white px-4 h-10 rounded-md text-sm font-medium flex items-center gap-2" data-testid="dashboard-new-project">
          <Briefcase size={16} /> Voir les projets
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
        <KpiCard testId="kpi-projects" icon={Briefcase} label="Projets actifs" value={k.projets_actifs} subtitle={`${k.projets_total} au total`} accent />
        <KpiCard testId="kpi-tasks-late" icon={AlertTriangle} label="Tâches en retard" value={k.taches_en_retard} subtitle={`${k.taches_total} tâches au total`} danger />
        <KpiCard testId="kpi-progress" icon={TrendingUp} label="Avancement moyen" value={`${k.avancement_moyen_pct}%`} subtitle="tous projets" />
        <KpiCard testId="kpi-rentability" icon={Activity} label="Rentabilité" value={`${k.rentabilite_pct}%`} subtitle="sur budget global" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-baseline justify-between mb-1">
            <h2 className="font-heading text-lg font-bold">Budget global — Prévu vs Réel</h2>
            <span className="text-xs text-slate-500 font-mono">USD ($)</span>
          </div>
          <p className="text-xs text-slate-500 mb-4">Comparaison cumulative sur tous les projets</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <BarChart data={budgetData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  <Cell fill="#0A2540" />
                  <Cell fill="#E05A3D" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-heading text-lg font-bold mb-1">Répartition par statut</h2>
          <p className="text-xs text-slate-500 mb-4">Pipeline projet</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <PieChart>
                <Pie data={statusData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {statusData.map((e, i) => <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1 mt-2">
            {statusData.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: STATUS_COLORS[i % STATUS_COLORS.length] }}></div>
                  <span className="text-slate-700">{s.name}</span>
                </div>
                <span className="font-mono font-semibold">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent projects */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-heading text-lg font-bold">Projets récents</h2>
            <p className="text-xs text-slate-500 mt-0.5">Mises à jour des 5 dernières missions</p>
          </div>
          <button onClick={() => navigate("/projects")} className="text-sm text-btp-navy font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="dashboard-view-all-projects">
            Tout voir <ArrowUpRight size={14} />
          </button>
        </div>
        <table className="btp-table">
          <thead>
            <tr>
              <th>Code</th><th>Projet</th><th>Client</th><th>Statut</th><th>Avancement</th><th>Budget</th>
            </tr>
          </thead>
          <tbody>
            {(stats.projets_recents || []).map((p) => (
              <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="cursor-pointer" data-testid={`recent-project-${p.code}`}>
                <td className="font-mono text-xs text-slate-500">{p.code}</td>
                <td className="font-medium">{p.name}</td>
                <td className="text-slate-600">{p.client}</td>
                <td><StatusBadge status={p.status} /></td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-btp-navy" style={{ width: `${(p.progress || 0) * 100}%` }}></div>
                    </div>
                    <span className="text-xs font-mono text-slate-600">{Math.round((p.progress || 0) * 100)}%</span>
                  </div>
                </td>
                <td className="font-mono text-xs">{fmtMoney(p.budget_prevu)}</td>
              </tr>
            ))}
            {(!stats.projets_recents || stats.projets_recents.length === 0) && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-12">Aucun projet — Créez-en un pour démarrer.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, accent, danger, testId }) {
  return (
    <div className="bg-white p-5 relative" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
          <div className={`mt-2 font-heading text-3xl font-bold tracking-tight ${danger ? "text-red-600" : "text-slate-900"}`}>{value}</div>
          <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent ? "bg-btp-terra/10 text-btp-terra" : danger ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}
